import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom'
import { Bell, ChevronDown, User } from 'lucide-react'
import Logo from '@/components/common/Logo'
import { useAuthStore } from '@/stores/authStore'
import { useUnreadCount } from '@/features/notification/api'
import { useCommunityBoards } from '@/features/community/publicApi'
import { useEffect, useState } from 'react'

export default function DesktopHeader() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, user, logout } = useAuthStore()
  const { data: unreadCount } = useUnreadCount()
  const { data: boards = [] } = useCommunityBoards()
  const [scrolled, setScrolled] = useState(false)

  const isHome = location.pathname === '/'

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // 홈에서는 투명 시작, 스크롤 시 흰색. 다른 페이지는 항상 흰색
  const isTransparent = isHome && !scrolled

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        isTransparent
          ? 'bg-transparent'
          : 'bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <Logo variant={isTransparent ? 'light' : 'dark'} size="lg" />
        </Link>

        {/* Nav — 고유 콘텐츠(블로그)와 도구를 앞쪽에 노출 */}
        <nav className="flex items-center gap-6">
          {[
            { to: '/facilities', label: '시설검색' },
            { to: '/care-calculator', label: '통합돌봄계산기' },
            { to: '/blog', label: '블로그' },
            { to: '/recommend', label: 'AI추천' },
            { to: '/mypage/care-log', label: '돌봄일지' },
            { to: '/checklist', label: '시설체크리스트' },
          ].map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `text-sm font-semibold transition-colors ${
                  isActive
                    ? isTransparent ? 'text-white' : 'text-cb-primary'
                    : isTransparent
                      ? 'text-white/70 hover:text-white'
                      : 'text-gray-600 hover:text-gray-900'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}

          {/* 요양비용 계산 — hover 드롭다운.
              「비용계산」만으로는 무엇의 비용인지 알 수 없어 「요양비용 계산」으로 바꿨다.
              시설·재가를 모두 다루므로 「시설이용요금」은 방문요양을 배제해 쓰지 않는다.
              등급 자가진단은 대메뉴에 자리가 없어 여기로 넣는다 — 등급을 모르는 사람이
              비용을 계산하려다 막히는 자리라 동선이 맞고, 링크 문구가 그 페이지의
              목표 검색어와 같아 내부 링크로도 값이 있다. */}
          <div className="group relative">
            <NavLink
              to="/cost-simulator"
              className={({ isActive }) =>
                `flex items-center gap-0.5 text-sm font-semibold transition-colors ${
                  isActive
                    ? isTransparent ? 'text-white' : 'text-cb-primary'
                    : isTransparent
                      ? 'text-white/70 hover:text-white'
                      : 'text-gray-600 hover:text-gray-900'
                }`
              }
            >
              요양비용 계산
              <ChevronDown size={14} className="opacity-70 transition-transform group-hover:rotate-180" />
            </NavLink>

            <div className="invisible absolute left-1/2 z-50 -translate-x-1/2 pt-3 opacity-0 transition-opacity group-hover:visible group-hover:opacity-100">
              <div className="min-w-[220px] rounded-xl border border-gray-100 bg-white py-2 shadow-lg ring-1 ring-black/5">
                {[
                  { to: '/cost-simulator', name: '요양비용 계산기', hint: '등급을 아실 때' },
                  { to: '/guide/self-assessment', name: '요양등급 자가진단', hint: '8문항 간단 확인' },
                ].map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="block px-4 py-2 hover:bg-cb-primary/5"
                  >
                    <span className="block text-sm text-gray-700">{item.name}</span>
                    {/* 통합돌봄계산기까지 등급을 알려주는 도구가 셋이라 한 줄로 차이를 밝힌다 */}
                    <span className="block text-xs text-gray-400">{item.hint}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* 커뮤니티 — hover 드롭다운 (게시판 동적 + 블로그) */}
          <div className="group relative">
            <NavLink
              to="/community"
              className={({ isActive }) =>
                `flex items-center gap-0.5 text-sm font-semibold transition-colors ${
                  isActive
                    ? isTransparent ? 'text-white' : 'text-cb-primary'
                    : isTransparent
                      ? 'text-white/70 hover:text-white'
                      : 'text-gray-600 hover:text-gray-900'
                }`
              }
            >
              커뮤니티
              <ChevronDown size={14} className="opacity-70 transition-transform group-hover:rotate-180" />
            </NavLink>

            {/* hover 간 빈틈으로 끊기지 않도록 투명 패딩 영역 포함 */}
            <div className="invisible absolute left-1/2 z-50 -translate-x-1/2 pt-3 opacity-0 transition-opacity group-hover:visible group-hover:opacity-100">
              <div className="min-w-[180px] rounded-xl border border-gray-100 bg-white py-2 shadow-lg ring-1 ring-black/5">
                {boards.length === 0 ? (
                  <div className="px-4 py-2 text-xs text-gray-400">불러오는 중…</div>
                ) : (
                  boards.map((b) => (
                    <Link
                      key={b.slug}
                      to={`/community/${b.slug}`}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-cb-primary/5 hover:text-cb-primary"
                    >
                      {b.name}
                    </Link>
                  ))
                )}
              </div>
            </div>
          </div>
        </nav>

        {/* Auth */}
        <div className="flex items-center gap-3">
          <a
            href="https://biz.carebalance.co.kr"
            target="_blank"
            rel="noopener noreferrer"
            className={`text-xs transition-colors ${
              isTransparent
                ? 'text-white/60 hover:text-white/90'
                : 'text-gray-400 hover:text-gray-700'
            }`}
          >
            시설관리자
          </a>
          {isAuthenticated ? (
            <>
              <Link
                to="/mypage/notifications"
                className={`relative p-2 rounded-lg transition-colors ${isTransparent ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
              >
                <Bell size={20} className={isTransparent ? 'text-white/80' : 'text-gray-600'} />
                {unreadCount != null && unreadCount > 0 && (
                  <span className="absolute top-0.5 right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-0.5 text-[9px] font-bold text-white">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Link>
              <Link
                to="/mypage"
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${isTransparent ? 'text-white/80 hover:text-white' : 'text-gray-700 hover:text-cb-primary'}`}
              >
                <User size={16} />
                {user?.name ?? '마이페이지'}
              </Link>
              <button
                onClick={() => { logout(); navigate('/') }}
                className={`text-sm transition-colors ${isTransparent ? 'text-white/50 hover:text-white/80' : 'text-gray-400 hover:text-gray-600'}`}
              >
                로그아웃
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className={`text-sm font-medium transition-colors ${isTransparent ? 'text-white/80 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              >
                로그인
              </Link>
              <Link
                to="/signup"
                className={`rounded-lg px-4 py-2 text-sm font-semibold transition-all ${
                  isTransparent
                    ? 'bg-white/20 text-white backdrop-blur-sm hover:bg-white/30 border border-white/30'
                    : 'bg-cb-primary text-white hover:bg-cb-primary/90 shadow-sm'
                }`}
              >
                회원가입
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  )
}
